'use client'

import { Separator } from '@acko/separator'
import { Typography } from '@acko/typography'
import { Fragment } from 'react'
import { BikeContinueInsuringCard } from './BikeContinueInsuringCard'
import { BikeRegistrationForm, type BikeRegistrationFormProps } from './BikeRegistrationForm'
import styles from './page.module.css'

const imgCircleArrowLine = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Circle_arrow_line_882d3f586d.svg'
const imgCircleArrowFilled = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Circle_arrow_filled_9d8fd2ddfd.svg'

interface HeroContentClientProps {
  continueInsuringJourney?: any
  continueInsuringSegmentEventProps?: object
  logoHref?: string
  logoSrc?: string
  onNewBikeClick: () => Promise<void> | void
  registrationFormProps: BikeRegistrationFormProps
  viewModel: any
}

export const HeroContentClient = ({
  continueInsuringJourney,
  continueInsuringSegmentEventProps = {},
  logoHref = '/',
  logoSrc,
  onNewBikeClick,
  registrationFormProps,
  viewModel,
}: HeroContentClientProps) => {
  const handleNewBikeKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      void onNewBikeClick()
    }
  }

  return (
    <>
      {logoSrc ? (
        <a href={logoHref} aria-label="ACKO home" className="inline-flex w-fit">
          <img alt="ACKO" className="h-24 w-auto" src={logoSrc} />
        </a>
      ) : null}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between w-full gap-20 lg:gap-32 min-w-0">
        <div className={`${styles.heroCol} flex flex-col gap-16 lg:gap-32 items-center lg:items-start w-full min-w-0`}>
          <div className={`${styles.productImageFrame} relative w-full flex items-center justify-center lg:order-2`}>
            <picture className={styles.heroPicture}>
              <source media="(min-width: 1024px)" srcSet={viewModel.heroImage.desktopSrc} />
              <img
                alt={viewModel.heroImage.alt}
                className={`${styles.productImage} lg:absolute lg:bottom-0 lg:left-0 object-contain lg:object-left-bottom`}
                src={viewModel.heroImage.mobileSrc}
              />
            </picture>
          </div>
          <div className="flex flex-col gap-20 items-center lg:items-start w-full lg:order-1">
            <div className="flex flex-col gap-8 items-center lg:items-start w-full text-center lg:text-left">
              <Typography
                variant="display-md"
                as="h1"
                weight="semibold"
                className="text-[var(--font-heading-lg-size)] leading-[var(--font-heading-lg-line)] lg:text-[var(--font-display-md-size)] lg:leading-[var(--font-display-md-line)] text-balance">
                {viewModel.heading}
              </Typography>
              <Typography variant="body-md" color="secondary" className="hidden lg:block">
                {viewModel.subHeading}
              </Typography>
              <Typography variant="body-md" color="secondary" className={`${styles.zeroCommissionText} lg:hidden`}>
                {viewModel.subHeading}
              </Typography>
            </div>
            <div className="hidden lg:flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-24 w-full min-w-0">
              {viewModel.features.map((feature, index) => (
                <Fragment key={feature.title || index}>
                  <div className="flex gap-12 items-center min-w-0">
                    <div
                      className={`${styles.shadowIcon} ${styles.surfaceElevated} flex items-center justify-center rounded-full shrink-0 w-48 h-48`}>
                      <img alt={feature.iconAlt} className="w-24 h-24 object-contain" src={feature.iconSrc} />
                    </div>
                    <div className="flex flex-col gap-4 min-w-0">
                      <Typography variant="heading-md" as="h3" color="primary">
                        {feature.title}
                      </Typography>
                      <Typography variant="body-md" color="secondary">
                        {feature.description}
                      </Typography>
                    </div>
                  </div>
                  {index < viewModel.features.length - 1 ? (
                    <Separator orientation="vertical" className="h-40 hidden sm:block shrink-0" decorative />
                  ) : null}
                </Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className={`${styles.widgetCol} flex flex-col gap-20 w-full min-w-0`}>
          {continueInsuringJourney ? (
            <BikeContinueInsuringCard
              item={continueInsuringJourney}
              commonSegmentEventProps={continueInsuringSegmentEventProps}
            />
          ) : null}
          <BikeRegistrationForm {...registrationFormProps} headingClassName="hidden lg:block" />
          <div
            className={`${styles.newBikeCard} group cursor-pointer transition-all duration-200 hover:shadow-lg`}
            role="button"
            tabIndex={0}
            onClick={() => {
              void onNewBikeClick()
            }}
            onKeyDown={handleNewBikeKeyDown}>
            <div className="flex flex-col gap-8">
              <Typography variant="heading-md" as="h3" color="primary">
                {viewModel.newFlowBanner.heading}
              </Typography>
              <Typography variant="body-md" color="secondary">
                {viewModel.newFlowBanner.subHeading}
              </Typography>
            </div>
            <div className="flex items-center justify-between gap-12 w-full">
              <div className={`${styles.chipGradient} flex gap-8 items-center pl-8 pr-12 py-8 rounded-full`}>
                <img
                  alt={viewModel.newFlowBanner.iconAlt}
                  className="w-24 h-24 object-contain shrink-0"
                  src={viewModel.newFlowBanner.iconSrc}
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
                <Typography variant="body-md" as="span" color="primary" className={styles.planStartingPrice}>
                  {viewModel.newFlowBanner.ctaText}
                </Typography>
              </div>
              <div className="relative w-24 h-24">
                <img
                  alt=""
                  className="w-24 h-24 object-contain transition-opacity duration-200 group-hover:opacity-0"
                  src={imgCircleArrowLine}
                />
                <img
                  alt=""
                  className="absolute inset-0 w-24 h-24 object-contain transition-opacity duration-200 opacity-0 group-hover:opacity-100"
                  src={imgCircleArrowFilled}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
